import { useId } from 'react';
import { cn } from '@/shared/utils';
import type { QuoteHistoryPoint } from '@/shared/types';

const VIEWBOX_WIDTH = 48;
const VIEWBOX_HEIGHT = 20;
const STROKE_WIDTH = 1.5;

type Props = {
  points: Array<QuoteHistoryPoint>;
  isPositive: boolean;
  className?: string;
};

const buildCoordinates = (points: Array<QuoteHistoryPoint>) => {
  const prices = points.map(point => point.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min;

  const usableHeight = VIEWBOX_HEIGHT - STROKE_WIDTH;
  const stepX =
    points.length > 1 ? VIEWBOX_WIDTH / (points.length - 1) : VIEWBOX_WIDTH;

  return points.map((point, index) => {
    const ratio = range === 0 ? 0.5 : (point.price - min) / range;

    return {
      x: points.length > 1 ? index * stepX : VIEWBOX_WIDTH / 2,
      y: STROKE_WIDTH / 2 + (1 - ratio) * usableHeight,
    };
  });
};

const MarketSparkline = ({ points, isPositive, className }: Props) => {
  const gradientId = useId();

  if (points.length === 0) {
    return (
      <span
        className={cn('block w-[4.8rem] h-[2rem] shrink-0', className)}
        aria-hidden
      />
    );
  }

  const coordinates = buildCoordinates(points);
  const line = coordinates
    .map(({ x, y }) => `${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ');
  const area = `M ${line.split(' ').join(' L ')} L ${VIEWBOX_WIDTH},${VIEWBOX_HEIGHT} L 0,${VIEWBOX_HEIGHT} Z`;

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      preserveAspectRatio="none"
      className={cn(
        'w-[4.8rem] h-[2rem] shrink-0',
        isPositive ? 'text-cyan' : 'text-red',
        className
      )}
      aria-hidden>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.24" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <polyline
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export { MarketSparkline };
